
import {
  Component,
  namespace,
  Prop,
  Vue,
  Watch,
} from "nuxt-property-decorator";
import RouterLink from "~/components/atoms/text/RouterLink.vue";
import { ButtonText } from "~/operations/messages";
import sendGtmEvent from "~/operations/send-gtm-event";
import { FuelProductType } from "~/types";
import { InputValues, SupportMessages } from "~/types/Pellets";
import { buildPelletsCheckoutQuery } from "~/utils/pelletsCheckout";
import { readPelletsPreset, writePelletsPreset } from "~/utils/pelletsStorage";
import { validateZipAndAmount } from "~/utils/priceFormValidation";

const CalculatorModule = namespace("calculator");

@Component({
  components: {
    RouterLink,
  },
})
export default class ReducedPriceComparison extends Vue {
  // Props with default values
  @Prop({ default: "small" }) variant!: "small" | "bordered";
  @Prop({ default: "all" }) paymentType!: string;
  @Prop({ default: "oil" }) inputType!: FuelProductType;
  @Prop({ default: "Heizöl-Preisvergleich:" }) titleText!: string;
  @Prop({ default: "Liter" }) amountLabel!: string;
  @Prop({ default: "Ihr Bedarf" }) amountPlaceholder!: string;
  @Prop({ default: ButtonText.compareOilPrices }) buttonText!: string;
  @Prop({ default: 3000 }) defaultAmount!: number;
  @Prop({ default: 500 }) minAmount!: number;
  @Prop({ default: 32000 }) maxAmount!: number;

  oilValues: InputValues = {
    zipCode: "",
    amount: this.defaultAmount,
  };

  pelletsValues: InputValues = {
    zipCode: "",
    amount: this.defaultAmount,
  };

  supportMessages: SupportMessages = {
    zipCode: "",
    amount: "",
  };

  // Vuex actions and state
  @CalculatorModule.Action("fetchAvailableOptions")
  fetchAvailableOptions!: any;

  @CalculatorModule.State("availabilities")
  availabilities!: any;

  mounted() {
    this.tryLocalStoragePreset();
  }

  updateZipCode(value: string): void {
    this.supportMessages.zipCode = "";
    if (this.inputType === "pellets") {
      this.pelletsValues.zipCode = value;
    } else {
      this.oilValues.zipCode = value;
    }
  }

  updateAmount(value: number): void {
    if (this.inputType === "pellets") {
      this.pelletsValues.amount = value;
    } else {
      this.oilValues.amount = value;
    }
  }

  tryLocalStoragePreset() {
    if (this.inputType === "pellets") {
      const preset = readPelletsPreset(this.minAmount);
      if (preset.zipCode) this.updateZipCode(preset.zipCode);
    } else {
      const storageKeys = this.getStorageKeys();
      const userPresetZipCode = localStorage.getItem(storageKeys.zipCode);
      if (userPresetZipCode?.length === 5) {
        this.updateZipCode(userPresetZipCode);
      }
      const userPresetAmount = Number(localStorage.getItem(storageKeys.amount));
      if (userPresetAmount && userPresetAmount >= this.minAmount) {
        this.updateAmount(userPresetAmount);
      }
    }
  }

  // Storage keys based on input type
  getStorageKeys() {
    return this.inputType === "pellets"
      ? { zipCode: "pelletsZipCode", amount: "pelletsAmount" }
      : // Keep original keys for oil
        { zipCode: "userZipCode", amount: "userOilAmount" };
  }

  setLocalStoragePreset(zipcode: string, amount: number) {
    if (this.inputType === "pellets") {
      writePelletsPreset(zipcode, amount);
    } else {
      const storageKeys = this.getStorageKeys();
      if (zipcode.length === 5) {
        localStorage.setItem(storageKeys.zipCode, zipcode);
      }
      localStorage.setItem(storageKeys.amount, amount.toString());
    }
  }

  setButtonVariant() {
    if (this.inputType === "pellets") return "pellets-primary";
    if (this.variant === "small") return "small";
    if (this.variant === "bordered") return "black";
    return "default";
  }

  setButtonSize() {
    return this.variant === "small" ? "small" : "default";
  }

  validateInputs() {
    return validateZipAndAmount(this.inputValues, this.supportMessages, {
      minAmount: this.minAmount,
      maxAmount: this.maxAmount,
      amountLabel: this.amountLabel,
    });
  }

  async redirectToComparsionPage() {
    if (!this.validateInputs()) {
      return;
    }

    try {
      await this.fetchAvailableOptions({
        isPellets: this.inputType === "pellets",
        zipcode: this.inputValues.zipCode,
        amount: this.inputValues.amount,
      });

      this.setLocalStoragePreset(
        this.inputValues.zipCode,
        Number(this.inputValues.amount),
      );

      this.$router.push({
        path:
          this.inputType === "pellets"
            ? "/pelletspreise/bestellung"
            : "/heizoelpreise/heizoel-bestellung",
        query: this.getQueryParams(),
      });

      this.sendAnalyticsEvent();
    } catch (error) {
      this.supportMessages.zipCode = "Ungültige Postleitzahl";
    }
  }

  getQueryParams(): Record<string, string> {
    if (this.inputType === "pellets") {
      return buildPelletsCheckoutQuery(
        this.inputValues.zipCode,
        this.inputValues.amount,
      );
    }

    return {
      zipcode: this.inputValues.zipCode,
      amount: this.inputValues.amount.toString(),
      unloading_points: "1",
      deliveryTimes: "normal",
      payment_type: this.availablePayment,
      prod: "normal",
      hose: "fortyMetre",
      short_vehicle: "withTrailer",
    };
  }

  sendAnalyticsEvent() {
    sendGtmEvent(this, {
      category: "Navigation",
      label: `Nav CTA ${this.inputType.toUpperCase()} PV`,
      action: "submit",
    });
  }

  @Watch("inputType")
  onInputTypeChange(): void {
    this.tryLocalStoragePreset();
  }

  get availablePayment() {
    return this.availabilities.paymentTypes?.includes(this.paymentType)
      ? this.paymentType
      : this.availabilities.paymentTypes?.[0] || "all";
  }

  get inputValues(): InputValues {
    return this.inputType === "pellets" ? this.pelletsValues : this.oilValues;
  }
}
